import { MutationCache, QueryClient, QueryClientProvider, type QueryKey } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import type { PropsWithChildren } from 'react'
import { toast } from 'sonner'

import { extractServerMessage, getErrorMessage } from '@/utils/error-message'

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      invalidatesQuery?: QueryKey
      successMessage?: string
      errorMessage?: string
    }
  }
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError(error, _, __, mutation) {
      // Always log the raw error so devs can diagnose unparseable server responses from DevTools.
      // eslint-disable-next-line no-console
      console.error('[Mutation error]', error)
      const serverMessage = extractServerMessage(error)
      if (serverMessage) {
        toast.error(serverMessage)
        return
      }
      // No structured body — fall back to the caller's generic message and append the HTTP status
      // so failures that the backend didn't annotate are still diagnosable.
      const fallback = mutation.meta?.errorMessage ?? getErrorMessage(error)
      const status = error instanceof AxiosError ? error.response?.status : null
      toast.error(status ? `${fallback} (HTTP ${status})` : fallback)
    },
    onSuccess(_, __, ___, mutation) {
      if (mutation.meta?.successMessage) {
        toast.success(mutation.meta.successMessage)
      }
    },
    onSettled(_, __, ___, ____, mutation) {
      if (mutation.options.meta) {
        mutation.options.meta?.invalidatesQuery?.map((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: [queryKey]
          })
        })
      }
    }
  }),
  defaultOptions: {
    queries: {
      retry: 1
    },
    mutations: {
      retry: 0
    }
  }
})

export const ReactQueryProvider = ({ children }: PropsWithChildren) => {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
